// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { cn } from '@/lib/utils';
import type { ActivityItemState } from '@web/lib/remoteActivityModel';
import type { ReactNode } from 'react';

const MARKER_CLASS: Record<ActivityItemState, string> = {
  completed:
    'bg-ds-bg-status-completed-subtle-default text-ds-text-status-completed-strong-default',
  active:
    'bg-ds-bg-status-running-subtle-default text-ds-text-status-running-strong-default',
  waiting:
    'bg-ds-bg-status-pending-subtle-default text-ds-text-status-pending-strong-default',
  failed:
    'bg-ds-bg-status-error-subtle-default text-ds-text-status-error-strong-default',
  interrupted:
    'bg-ds-bg-status-paused-subtle-default text-ds-text-status-paused-strong-default',
  neutral:
    'bg-ds-bg-neutral-default-default text-ds-icon-neutral-muted-default',
};

export function TimelineConnector({
  state,
  last,
  children,
}: {
  state: ActivityItemState;
  last: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-10 w-7 shrink-0 flex-col items-center">
      <span
        className={cn(
          'relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
          MARKER_CLASS[state]
        )}
      >
        {children}
      </span>
      {!last && (
        <span
          className={cn(
            'my-1 min-h-4 w-px flex-1 bg-ds-border-neutral-subtle-default',
            state === 'active' && 'bg-ds-bg-status-running-default-default'
          )}
          aria-hidden
        />
      )}
    </div>
  );
}
